
import { useState } from 'react';
import { adjustProductStock } from '@/lib/supabase/inventory';
import { useToast } from '@/components/ui/use-toast';

export type AdjustmentType = 'add' | 'remove';

interface UseStockAdjustmentProps {
  productId: string;
  currentStock: number;
  onStockUpdated: (newQuantity: number) => void;
  onClose: () => void;
}

export const predefinedReasons = [
  { value: 'purchase', label: 'Purchase/Restock' },
  { value: 'sale', label: 'Sale' },
  { value: 'return', label: 'Customer Return' },
  { value: 'damaged', label: 'Damaged/Defective' },
  { value: 'lost', label: 'Lost/Missing' },
  { value: 'audit', label: 'Inventory Audit' },
  { value: 'other', label: 'Other (Please Specify)' },
];

export function useStockAdjustment({
  productId,
  currentStock,
  onStockUpdated,
  onClose
}: UseStockAdjustmentProps) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('add');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('purchase');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleAdjustStock = async () => {
    if (quantity <= 0) {
      toast({
        title: 'Invalid quantity',
        description: 'Please enter a quantity greater than zero',
        variant: 'destructive',
      });
      return;
    }

    if (adjustmentType === 'remove' && quantity > currentStock) {
      toast({
        title: 'Insufficient stock',
        description: `You can't remove more than the current stock (${currentStock})`,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedReason = predefinedReasons.find(r => r.value === reason)?.label || reason;
      
      const result = await adjustProductStock({
        productId,
        quantityChange: adjustmentType === 'add' ? quantity : -quantity,
        reason: selectedReason,
        notes,
      });

      if (result.success && result.newQuantity !== undefined) {
        toast({
          title: 'Stock updated',
          description: `Inventory successfully ${adjustmentType === 'add' ? 'increased' : 'decreased'} by ${quantity}`,
        });
        onStockUpdated(result.newQuantity);
        onClose();
        resetForm();
      } else {
        toast({
          title: 'Update failed',
          description: result.error || 'Failed to update inventory',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adjusting stock:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setAdjustmentType('add');
    setQuantity(1);
    setReason('purchase');
    setNotes('');
  };

  return {
    adjustmentType,
    setAdjustmentType,
    quantity,
    setQuantity,
    reason,
    setReason,
    notes,
    setNotes,
    isSubmitting,
    handleAdjustStock,
  };
}
